import React, {useEffect, useState} from "react";
import ErrorDisplay from "../../error-display";
import {getItem, postRaw} from "../../../utils/communication";
import HFInput from "../../form/hf-input";
import HfUnmarked from "../../form/hf-unmarked";
import HFSelect from "../../form/hf-select";
import HFStepper from "../../form/hf-stepper";
import parseAnError from "../../../model/misc";
import RecipeIngredientEdit, {RecipeIngredientData} from "../ingredient/recipe-ingredients-edit";
import RecipeToolEdit, {RecipeToolData} from "../tool/recipe-tools-edit";
import {RecipeIngredientDataArray, RecipeToolDataArray} from "../../../utils/comparable";
import nanoid from "../../../utils/nanoid";
import {clone} from "../../../utils/object";

interface RecipeEditProps {
    name: string;
    description: string;
    steps: string;
    tips: string;
    cuisineTypeId: string;
    estimatedPreparationTime: string;
    estimatedCookingTime: string;
    servings: number;
    rating: number;
    deliciousness: number;
    difficulty: number;
    ingredients: RecipeIngredientData[];
    tools: RecipeToolData[];
}

export default function RecipeEditContent({id, onSubmitted}: { id: string, onSubmitted: () => void }) {
    const [recipe, setRecipe] = useState<Recipe>(),
        [formData, setFormData] = useState<RecipeEditProps>({
            name: '',
            description: '',
            steps: '',
            tips: '',
            cuisineTypeId: '',
            estimatedPreparationTime: '',
            estimatedCookingTime: '',
            servings: 0,
            rating: 0,
            deliciousness: 0,
            difficulty: 0,
            ingredients: [],
            tools: []
        }), [cuisineTypes, setCuisineTypes] = useState<CuisineType[]>([]),
        [loading, setLoading] = useState(true),
        [submitting, setSubmitting] = useState(false),
        [error, setError] = useState<string | null>(null),
        [oldIngredients, setOldIngredients] = useState<RecipeIngredientData[]>([]),
        [oldTools, setOldTools] = useState<RecipeToolData[]>([]);

    useEffect(() => {
        (async () => {
            setLoading(true);

            if (id === "new") {
                setRecipe({
                    publicId: "",
                    name: "",
                    description: "",
                    steps: "",
                    tips: "",
                    cuisineTypeId: "",
                    estimatedTime: "",
                    servingsMultiplier: 0,
                    rating: 0,
                    deliciousness: 0,
                    difficulty: 0,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                });

                setLoading(true);
                return;
            }

            let response = await getItem<Recipe>("/cooking/recipe/" + id + ":/");

            if (response.code !== 200) {
                setError(parseAnError(response.data).message);
            } else {
                let recipe = response.data as Recipe;
                setRecipe(recipe);
            }

            setLoading(false);
        })();
    }, [id]);

    useEffect(() => {
        (async () => {
            setLoading(true);

            let response = await getItem<CuisineType[]>("/cooking/cuisineType/all");

            if (response.code === 200) {
                const cuisineTypes = response.data as CuisineType[];

                setCuisineTypes(cuisineTypes);

                if (id === "new") {
                    setFormData({
                        ...formData,
                        cuisineTypeId: cuisineTypes[0].publicId
                    });
                }
            }

            setLoading(false);
        })();
    }, []);

    useEffect(() => {
        setLoading(true);

        const estimatedTimeComponents = recipe?.estimatedTime ? recipe.estimatedTime.split("+") : ["", ""];

        const _oldIngredients = recipe?.ingredients?.map(ri => {
            return {
                ingredient: ri.ingredient!,
                ingredientId: ri.ingredient!.publicId,
                amount: ri.amount,
                unit: ri.unit
            };
        }) ?? [];
        setOldIngredients(_oldIngredients);

        const _oldTools = recipe?.tools?.map(ri => {
            return {
                tool: ri.tool!,
                toolId: ri.tool!.publicId,
                amount: ri.amount,
                unit: ri.unit
            };
        }) ?? [];
        setOldTools(_oldTools);

        setFormData({
            name: recipe?.name ?? "",
            description: recipe?.description ?? "",
            steps: recipe?.steps ?? "",
            tips: recipe?.tips ?? "",
            cuisineTypeId: recipe?.cuisineType?.publicId ?? "",
            estimatedPreparationTime: estimatedTimeComponents[0],
            estimatedCookingTime: estimatedTimeComponents[1],
            servings: recipe?.servingsMultiplier ?? 0,
            rating: recipe?.rating ?? 0,
            deliciousness: recipe?.deliciousness ?? 0,
            difficulty: recipe?.difficulty ?? 0,
            ingredients: clone(_oldIngredients),
            tools: clone(_oldTools)
        });

        setLoading(false);
    }, [recipe?.publicId]);

    const handleInputChange = (event: { target: { name: any; value: any; }; }) => {
        const {name, value} = event.target;
        setFormData({...formData, [name]: value});
    };

    const handleSelectChange = (name: string, value: string) => {
        setFormData({...formData, [name]: value});
    }

    const handleStepperChange = (name: string, value: number) => {
        setFormData({...formData, [name]: value});
    }

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitting(true);

        await (async () => {
            let {
                newItems,
                updatedItems,
                deletedItems
            } = new RecipeIngredientDataArray(oldIngredients).compareWith(formData.ingredients.map(ri => {
                return {
                    ingredientId: ri.ingredient!.publicId,
                    amount: ri.amount,
                    unit: ri.unit,
                };
            }));

            if (newItems.length) {
                let newIngredientsResult = await postRaw(`/cooking/recipeIngredient/all`, newItems.map((rid): RecipeIngredient => {
                    return {
                        publicId: nanoid(),
                        name: "",
                        recipeId: recipe!.publicId,
                        ingredientId: rid.ingredientId,
                        amount: rid.amount,
                        unit: rid.unit,
                        createdAt: new Date(),
                        updatedAt: new Date(),
                        deletedAt: null
                    };
                }));

                if (newIngredientsResult.code !== 200) {
                    setError(parseAnError(newIngredientsResult.data).message);
                    setSubmitting(false);
                    return;
                }
            }

            if (updatedItems.length + deletedItems.length) {
                let updatedIngredientsResult = await postRaw(`/cooking/recipeIngredient/update`, updatedItems.map(ri => {
                    ri.recipeId = recipe!.publicId;
                    return ri;
                }).concat(deletedItems.map(di => {
                    di.recipeId = recipe!.publicId;
                    di.deletedAt = new Date();
                    return di;
                })));

                if (updatedIngredientsResult.code !== 200) {
                    setError(parseAnError(updatedIngredientsResult.data).message);
                    setSubmitting(false);
                    return;
                }
            }
        })();

        await (async () => {
            let {
                newItems,
                updatedItems,
                deletedItems
            } = new RecipeToolDataArray(oldTools).compareWith(formData.tools.map(ri => {
                return {
                    toolId: ri.tool!.publicId,
                    amount: ri.amount,
                    unit: ri.unit,
                };
            }));

            if (newItems.length) {
                let newToolsResult = await postRaw(`/cooking/recipeTool/all`, newItems.map((rid): RecipeTool => {
                    return {
                        publicId: nanoid(),
                        name: "",
                        recipeId: recipe!.publicId,
                        toolId: rid.toolId,
                        amount: rid.amount,
                        unit: rid.unit,
                        createdAt: new Date(),
                        updatedAt: new Date(),
                        deletedAt: null
                    };
                }));

                if (newToolsResult.code !== 200) {
                    setError(parseAnError(newToolsResult.data).message);
                    setSubmitting(false);
                    return;
                }
            }

            if (updatedItems.length + deletedItems.length) {
                let updatedToolsResult = await postRaw(`/cooking/recipeTool/update`, updatedItems.map(ui => {
                    ui.recipeId = recipe!.publicId;
                    return ui;
                }).concat(deletedItems.map(di => {
                    di.recipeId = recipe!.publicId;
                    di.deletedAt = new Date();
                    return di;
                })));

                if (updatedToolsResult.code !== 200) {
                    setError(parseAnError(updatedToolsResult.data).message);
                    setSubmitting(false);
                    return;
                }
            }
        })();

        let response = await postRaw(`/cooking/recipe/${id === "new" ? nanoid() : id}:/`, {
            name: formData.name,
            description: formData.description,
            steps: formData.steps,
            tips: formData.tips,
            cuisineTypeId: formData.cuisineTypeId,
            estimatedTime: formData.estimatedPreparationTime + "+" + formData.estimatedCookingTime,
            servingsMultiplier: formData.servings,
            rating: formData.rating,
            deliciousness: formData.deliciousness,
            difficulty: formData.difficulty,
            createdAt: recipe?.createdAt,
            updatedAt: recipe?.updatedAt,
            deletedAt: recipe?.deletedAt
        });

        if (response.code !== 200) {
            setError(parseAnError(response.data).message);
            setSubmitting(false);
        } else {
            onSubmitted();
        }
    }

    if (loading) {
        return <div>Loading...</div>
    } else {
        return (
            <form onSubmit={onSubmit}>
                <HFInput type="text" name="name"
                         value={formData.name} onChange={handleInputChange}
                         placeholder="Name" label="Name"/>

                <HFSelect name="cuisineType"
                          value={formData.cuisineTypeId} onChange={handleSelectChange}
                          label="Cuisine Type"
                          options={cuisineTypes.map(ct => {
                              return {value: ct.publicId, label: ct.name};
                          })}/>

                <div className="grouped-input">
                    <HFInput type="text" name="estimatedPreparationTime"
                             value={formData.estimatedPreparationTime} onChange={handleInputChange}
                             placeholder="Preparation Time" label="Preparation Time"/>
                    <HFInput type="text" name="estimatedCookingTime"
                             value={formData.estimatedCookingTime} onChange={handleInputChange}
                             placeholder="Cooking Time" label="Cooking Time"/>
                </div>

                <RecipeIngredientEdit ingredients={formData.ingredients}
                                      setIngredients={(ingredients) => setFormData({
                                          ...formData,
                                          ingredients: ingredients
                                      })}/>

                <RecipeToolEdit tools={formData.tools}
                                setTools={(tools) => setFormData({
                                    ...formData,
                                    tools: tools
                                })}/>

                <div className="grouped-input">
                    <HFStepper name="rating" value={formData.rating} step={5} range={[0, 50]}
                               onChange={handleStepperChange} label="Rating"/>
                    <HFStepper name="deliciousness" value={formData.deliciousness} step={5} range={[0, 50]}
                               onChange={handleStepperChange} label="Deliciousness"/>
                    <HFStepper name="difficulty" value={formData.difficulty} step={5} range={[0, 50]}
                               onChange={handleStepperChange} label="Difficulty"/>
                </div>

                <HfUnmarked name="description" value={formData.description} onChange={handleInputChange}
                            placeholder="Description" label="Description"/>

                <HfUnmarked name="steps" value={formData.steps} onChange={handleInputChange} placeholder="Steps"
                            label="Steps"/>
                <HfUnmarked name="tips" value={formData.tips} onChange={handleInputChange} placeholder="Tips"
                            label="Tips"/>

                <div className="actions">
                    <button type="submit" disabled={submitting}>{submitting ? "Saving" : "Save"}</button>
                    <button type="reset">Reset</button>
                </div>

                <ErrorDisplay error={error}/>
            </form>
        );
    }
}